import { useEffect, useState } from 'react'
import { Badge } from '@quebon/ui/components/badge'
import { Input } from '@quebon/ui/components/input'

export const MAX_FIXED_ORDER = 999

export type FixedOrderParseResult =
  | { ok: true; order: number | null }
  | { ok: false }

/** 입력값 → 고정 순서. 빈 값은 해제(null), 1~999 정수만 허용 */
export function parseFixedOrderInput(value: string): FixedOrderParseResult {
  const trimmed = value.trim()
  if (trimmed === '') return { ok: true, order: null }
  if (!/^\d+$/.test(trimmed)) return { ok: false }
  const order = Number(trimmed)
  if (order < 1 || order > MAX_FIXED_ORDER) return { ok: false }
  return { ok: true, order }
}

interface StatusProps {
  order: number | null
  published: boolean
}

/** 고정 상태 배지: 비공개면 학습자에게 보이지 않는다는 점을 텍스트로 알린다 */
export function LcPlaygroundFixedStatus({ order, published }: StatusProps) {
  if (order == null) return null
  if (!published) {
    return (
      <Badge variant="destructive" title="비공개 게임은 고정해도 LC 학습자에게 노출되지 않습니다.">
        비공개라 미노출
      </Badge>
    )
  }
  return <Badge variant="secondary">고정</Badge>
}

interface Props {
  gameName: string
  order: number | null
  published: boolean
  disabled?: boolean
  onSave: (order: number | null) => void
  onInvalid: () => void
}

export function LcPlaygroundFixedOrderCell({
  gameName,
  order,
  published,
  disabled,
  onSave,
  onInvalid,
}: Props) {
  const [draft, setDraft] = useState(order == null ? '' : String(order))

  useEffect(() => {
    setDraft(order == null ? '' : String(order))
  }, [order])

  function commit() {
    const parsed = parseFixedOrderInput(draft)
    if (!parsed.ok) {
      onInvalid()
      setDraft(order == null ? '' : String(order))
      return
    }
    if (parsed.order === order) return
    onSave(parsed.order)
  }

  return (
    <div className="flex items-center justify-center gap-2">
      <Input
        type="number"
        inputMode="numeric"
        min={1}
        max={MAX_FIXED_ORDER}
        step={1}
        value={draft}
        placeholder="-"
        disabled={disabled}
        aria-label={`${gameName} LC 놀이터 고정 순서`}
        className="w-16 text-center tabular-nums"
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur()
          if (e.key === 'Escape') {
            setDraft(order == null ? '' : String(order))
          }
        }}
      />
      <LcPlaygroundFixedStatus order={order} published={published} />
    </div>
  )
}
